import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import type { Question, QuizSession, QuizBank, QuizConfig } from '../types';
import { shuffleQuestions, shuffleOptions, isAnswerCorrect, saveToStorage, loadFromStorage, removeDuplicateQuestions, getRecentQuestionIds, addToQuizHistory } from '../utils';
import { useApp } from './AppContext';

interface QuizContextType {
  session: QuizSession | null;
  startQuiz: (bank: QuizBank, count: number, groupId?: string, mode?: 'default' | 'exam' | 'homework' | 'halfExam') => void;
  startCustomQuiz: (config: QuizConfig) => void;
  startQuizWithQuestions: (questions: Question[], mode?: 'default' | 'exam' | 'homework' | 'halfExam', label?: string) => void;
  selectAnswer: (questionId: string, option: string) => void;
  goNext: () => void;
  goPrev: () => void;
  jumpTo: (index: number) => void;
  finishQuiz: () => void;
  clearSession: () => void;
  currentQuestion: Question | null;
  isCorrectAtCurrent: boolean | null;
  progress: number;
  correctCount: number;
  answeredCount: number;
  totalCount: number;
  elapsedTime: number;
  formattedTime: string;
  wrongAttempts: Record<string, number>;
}

const QuizContext = createContext<QuizContextType | null>(null);

export function QuizProvider({ children }: { children: ReactNode }) {
  const { state } = useApp();
  const [session, setSession] = useState<QuizSession | null>(null);
  const sessionRef = useRef<QuizSession | null>(null);
  // Keep ref in sync so callbacks always see the latest session value
  useEffect(() => { sessionRef.current = session; }, [session]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoNextRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAnsweredRef = useRef<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Restore session
  useEffect(() => {
    const saved = loadFromStorage<QuizSession | null>('currentSession', null);
    if (saved && saved.questions?.length > 0) {
      setSession({ ...saved, wrongAttempts: saved.wrongAttempts || {} });
      // Initialize elapsed from saved data so timer doesn't reset
      if (!saved.isFinished && saved.elapsedTime) {
        setElapsed(saved.elapsedTime);
      }
    }
  }, []);

  // Timer: based on accumulated elapsedTime, not mount time
  useEffect(() => {
    if (session && !session.isFinished) {
      timerRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 1000;
          // Persist elapsedTime so resume can continue
          setSession(curr => {
            if (!curr) return curr;
            const updated = { ...curr, elapsedTime: next };
            saveToStorage('currentSession', updated);
            return updated;
          });
          return next;
        });
      }, 1000);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }
  }, [session?.startTime, session?.isFinished]);

  const startQuiz = useCallback(
    (bank: QuizBank, count: number, groupId?: string, mode?: 'default' | 'exam' | 'homework' | 'halfExam') => {
      if (sessionRef.current && !sessionRef.current.isFinished) return;
      let questions = groupId
        ? (bank.hqb.groups.find(g => g.id === groupId)?.questionIds
            .map(id => bank.hqb.questions.find(q => q.id === id))
            .filter(Boolean) as Question[])
        : [...bank.hqb.questions];

      questions = removeDuplicateQuestions(questions);

      const recentIds = getRecentQuestionIds(bank.id);
      const availableQuestions = recentIds.length > 0
        ? questions.filter(q => !recentIds.includes(q.id))
        : questions;

      if (availableQuestions.length < count && availableQuestions.length < questions.length) {
        const needed = count - availableQuestions.length;
        const excluded = questions.filter(q => recentIds.includes(q.id));
        availableQuestions.push(...shuffleQuestions(excluded).slice(0, needed));
      }

      const targetQuestions = availableQuestions.length > 0 ? availableQuestions : questions;

      if (state.settings.randomBank) {
        questions = shuffleQuestions(targetQuestions);
      } else if (state.settings.shuffleQuestions) {
        questions = shuffleQuestions(targetQuestions);
      } else {
        questions = targetQuestions;
      }
      if (state.settings.shuffleOptions) {
        questions = questions.map(shuffleOptions);
      }

      const sliced = count > 0 && count < questions.length ? questions.slice(0, count) : questions;

      addToQuizHistory(bank.id, sliced.map(q => q.id));

      setSession({
        bankId: bank.id,
        mode: mode || undefined,
        questions: sliced,
        currentIndex: 0,
        answers: {},
        wrongAttempts: {},
        isFinished: false,
        startTime: new Date().toISOString(),
        elapsedTime: 0,
      });
      saveToStorage('currentSession', {
        bankId: bank.id,
        mode: mode || undefined,
        questions: sliced,
        currentIndex: 0,
        answers: {},
        wrongAttempts: {},
        isFinished: false,
        startTime: new Date().toISOString(),
        elapsedTime: 0,
      });
    },
    [state.settings, state.banks]
  );

  const startCustomQuiz = useCallback(
    (config: QuizConfig) => {
      let allQuestions: Question[] = [];

      for (const sel of config.selections) {
        const bank = state.banks.find((b) => b.id === sel.bankId);
        if (!bank) continue;
        if (sel.groupIds && sel.groupIds.length > 0) {
          const ids = new Set(sel.groupIds.flatMap(gid =>
            bank.hqb.groups.find((g) => g.id === gid)?.questionIds || []
          ));
          allQuestions.push(...bank.hqb.questions.filter((q) => ids.has(q.id)));
        } else {
          allQuestions.push(...bank.hqb.questions);
        }
      }

      allQuestions = removeDuplicateQuestions(allQuestions);

      const bankId = config.selections.map(s => s.bankId).join(',');
      const recentIds = getRecentQuestionIds(bankId);
      const availableQuestions = recentIds.length > 0
        ? allQuestions.filter(q => !recentIds.includes(q.id))
        : allQuestions;

      if (availableQuestions.length < config.count && availableQuestions.length < allQuestions.length) {
        const needed = config.count - availableQuestions.length;
        const excluded = allQuestions.filter(q => recentIds.includes(q.id));
        availableQuestions.push(...shuffleQuestions(excluded).slice(0, needed));
      }

      const targetQuestions = availableQuestions.length > 0 ? availableQuestions : allQuestions;

      const shuffleQ = config.shuffleQuestions ?? state.settings.shuffleQuestions;
      const shuffleO = config.shuffleOptions ?? state.settings.shuffleOptions;

      let questions = shuffleQ ? shuffleQuestions(targetQuestions) : targetQuestions;
      if (shuffleO) questions = questions.map(shuffleOptions);

      const count = config.count > 0 ? Math.min(config.count, questions.length) : questions.length;
      const sliced = questions.slice(0, count);

      addToQuizHistory(bankId, sliced.map(q => q.id));

      setSession({
        bankId: bankId,
        mode: config.mode || 'default',
        questions: sliced,
        currentIndex: 0,
        answers: {},
        wrongAttempts: {},
        isFinished: false,
        startTime: new Date().toISOString(),
        elapsedTime: 0,
      });
      saveToStorage('currentSession', {
        bankId: bankId,
        mode: config.mode || 'default',
        questions: sliced,
        currentIndex: 0,
        answers: {},
        wrongAttempts: {},
        isFinished: false,
        startTime: new Date().toISOString(),
        elapsedTime: 0,
      });
    },
    [state.settings, state.banks]
  );

  const startQuizWithQuestions = useCallback(
    (questions: Question[], mode?: 'default' | 'exam' | 'homework' | 'halfExam', label?: string) => {
      if (!questions.length) return;
      const deduplicated = removeDuplicateQuestions(questions);
      const bankId = label || 'custom';
      addToQuizHistory(bankId, deduplicated.map(q => q.id));
      const newSession: QuizSession = {
        bankId: bankId,
        mode: mode || 'default',
        questions: deduplicated,
        currentIndex: 0,
        answers: {},
        wrongAttempts: {},
        isFinished: false,
        startTime: new Date().toISOString(),
        elapsedTime: 0,
      };
      setSession(newSession);
      saveToStorage('currentSession', newSession);
    },
    []
  );

  const selectAnswer = useCallback(
    (questionId: string, option: string) => {
      setSession(prev => {
        if (!prev) return prev;
        const question = prev.questions.find(q => q.id === questionId);
        if (!question) return prev;
        const existing = prev.answers[questionId] || [];
        let newSelected: string[];
        if (question.type === 'multiple') {
          newSelected = existing.includes(option) ? existing.filter(o => o !== option) : [...existing, option];
        } else {
          newSelected = [option];
        }
        const updated = { ...prev, answers: { ...prev.answers, [questionId]: newSelected } };
        lastAnsweredRef.current = questionId;

        // Track wrong attempts: only count when answer is fully formed and wrong
        let wrongAttempts = prev.wrongAttempts;
        if (newSelected.length > 0) {
          const wrong = !isAnswerCorrect(question, newSelected);
          const isMultiIncomplete = question.type === 'multiple' && newSelected.length < question.answer.length;
          if (wrong && !isMultiIncomplete) {
            wrongAttempts = { ...prev.wrongAttempts, [questionId]: (prev.wrongAttempts[questionId] || 0) + 1 };
          }
        }
        const final = { ...updated, wrongAttempts };
        saveToStorage('currentSession', final);
        return final;
      });
    },
    []
  );

  const goNext = useCallback(() => {
    if (autoNextRef.current) { clearTimeout(autoNextRef.current); autoNextRef.current = null; }
    setSession(prev => {
      if (!prev || prev.currentIndex >= prev.questions.length - 1) return prev;
      const updated = { ...prev, currentIndex: prev.currentIndex + 1 };
      saveToStorage('currentSession', updated);
      return updated;
    });
  }, []);

  const goPrev = useCallback(() => {
    if (autoNextRef.current) { clearTimeout(autoNextRef.current); autoNextRef.current = null; }
    lastAnsweredRef.current = null;
    setSession(prev => {
      if (!prev || prev.currentIndex <= 0) return prev;
      const updated = { ...prev, currentIndex: prev.currentIndex - 1 };
      saveToStorage('currentSession', updated);
      return updated;
    });
  }, []);

  const jumpTo = useCallback((index: number) => {
    if (autoNextRef.current) { clearTimeout(autoNextRef.current); autoNextRef.current = null; }
    setSession(prev => {
      if (!prev || index < 0 || index >= prev.questions.length) return prev;
      const updated = { ...prev, currentIndex: index };
      saveToStorage('currentSession', updated);
      return updated;
    });
  }, []);

  const finishQuiz = useCallback(() => {
    if (autoNextRef.current) clearTimeout(autoNextRef.current);
    setSession(prev => {
      if (!prev) return prev;
      const updated = { ...prev, isFinished: true };
      saveToStorage('currentSession', updated);
      return updated;
    });
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
    sessionRef.current = null; // instant sync so subsequent startQuiz won't be blocked by stale ref
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoNextRef.current) clearTimeout(autoNextRef.current);
    setElapsed(0);
    saveToStorage('currentSession', null);
  }, []);

  const currentQuestion = session ? session.questions[session.currentIndex] || null : null;

  const isCorrectAtCurrent: boolean | null = currentQuestion && session
    ? (() => {
        // For exam mode, never reveal correctness during quiz
        if (session.mode === 'exam') return null;
        const selected = session.answers[currentQuestion.id];
        if (!selected || selected.length === 0) return null;
        if (currentQuestion.type === 'multiple' && selected.length < currentQuestion.answer.length) return null;
        return isAnswerCorrect(currentQuestion, selected);
      })()
    : null;

  const progress = session ? (session.currentIndex + 1) / session.questions.length : 0;
  const correctCount = session
    ? session.questions.filter(q => { const a = session.answers[q.id]; return a && isAnswerCorrect(q, a); }).length
    : 0;
  const answeredCount = session
    ? session.questions.filter(q => { const a = session.answers[q.id]; return a && a.length > 0; }).length
    : 0;
  const totalCount = session ? session.questions.length : 0;
  const formattedTime = elapsed > 0
    ? `${String(Math.floor(elapsed / 60000)).padStart(2, '0')}:${String(Math.floor((elapsed % 60000) / 1000)).padStart(2, '0')}`
    : '';

  // Auto-next: only trigger for the question that was just answered
  useEffect(() => {
    if (!session || session.isFinished) return;
    if (session.mode === 'exam') return;
    const q = session.questions[session.currentIndex];
    if (!q) return;
    if (q.id !== lastAnsweredRef.current) return;
    const selected = session.answers[q.id];
    if (!selected?.length) return;
    if (!isAnswerCorrect(q, selected)) return;
    if (!state.settings.autoNext) return;
    if (session.currentIndex >= session.questions.length - 1) {
      autoNextRef.current = setTimeout(() => finishQuiz(), state.settings.autoNextDelay);
      return;
    }
    autoNextRef.current = setTimeout(() => goNext(), state.settings.autoNextDelay);
    return () => { if (autoNextRef.current) clearTimeout(autoNextRef.current); };
  }, [session?.currentIndex, session?.answers[session?.questions?.[session?.currentIndex ?? 0]?.id ?? '']?.join(','), state.settings.autoNext, state.settings.autoNextDelay, session?.mode]);

  return (
    <QuizContext.Provider
      value={{
        session, startQuiz, startCustomQuiz, startQuizWithQuestions, selectAnswer, goNext, goPrev, jumpTo,
        finishQuiz, clearSession, currentQuestion, isCorrectAtCurrent,
        progress, correctCount, answeredCount, totalCount, elapsedTime: elapsed, formattedTime,
        wrongAttempts: session?.wrongAttempts || {},
      }}
    >
      {children}
    </QuizContext.Provider>
  );
}

export function useQuiz() {
  const context = useContext(QuizContext);
  if (!context) throw new Error('useQuiz must be used within QuizProvider');
  return context;
}
